import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, ListGroup, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as CONST from "../../../component/config/Constant";
import { AuthContext } from "../../../component/context/AuthContext";
 
// --- Sortable Item Component ---
function SortableItem({ id, title, index }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
 
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
    zIndex: transform ? 999 : "auto",
  };
 
  return (
    <ListGroup.Item
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="d-flex align-items-center border mb-2 rounded shadow-sm bg-white comment-item-hover"
    >
      <div className="me-3 text-muted fw-bold">{index + 1}.</div>
      <div className="flex-grow-1">{title}</div>
      {/* <div className="text-muted">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </div> */}
    </ListGroup.Item>
  );
}
 
// --- Main Reorder Modal ---
export default function ReorderArticlesModal({
  show,
  handleClose,
  technologyId,
  techName,
}) {
  const [items, setItems] = useState([]);
  const [allVersions, setAllVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [saving, setSaving] = useState(false);
  const [authUserData] = useContext(AuthContext);
  const isToken = authUserData?.[0]?.token || "";
 
  // 1. Initial Load: Fetch versions when modal opens
useEffect(() => {
  if (show) {
    // Reset the selection to the placeholder every time modal opens
    setSelectedVersion("");

    if (isToken && technologyId) {
      getVersions();
    }
  }
}, [show, isToken, technologyId]);
 
  // 2. Secondary Load: Fetch questions when selectedVersion changes
  useEffect(() => {
    if (selectedVersion) {
      fetchApprovedQuestions();
    } else {
      setItems([]); // Clear list if no version is selected
    }
  }, [selectedVersion]);
 
  const getVersions = async () => {
    try {
      const apiUrl = CONST.GET_TECHNOLOGY_VERSIONS + `${technologyId}`;
      const res = await axios({
        method: "get",
        url: apiUrl,
        headers: { Authorization: `Bearer ${isToken}` },
      });
 
      if (res.data.status === "success") {
        const optionsVersion = res.data.data.map((v) => ({
          label: v.version,
          value: v.version, // Use v.version or v.id based on what your Question List API expects
        }));
        setAllVersions(optionsVersion);
 
        // Auto-select the first version if available
        // if (optionsVersion.length > 0) {
        //   setSelectedVersion(optionsVersion[0].value);
        // }
      }
    } catch (error) {
      console.error("Error fetching versions:", error);
    }
  };
 
const fetchApprovedQuestions = async () => {
  try {
    const res = await axios.post(
      CONST.GET_TECH_QUESTIONS,
      {
        technology_id: technologyId,
        version: selectedVersion,
        limit: 1000,
        page_number: 1,
      },
      { headers: { Authorization: `Bearer ${isToken}` } },
    );
 
    const rawData = res.data?.data || [];
 
    // Map the nested data to a flat structure for the UI
    const flatItems = rawData.map((item) => ({
      id: item.question_id, // Use question_id as the drag-and-drop ID
      title: item.questionData?.title || "Untitled Question",
      display_order: item.display_order,
    }));
 
    // The backend already sorts by display_order, so we just set it
    setItems(flatItems);
  } catch (err) {
    console.error("Fetch error", err);
    setItems([]);
  }
};
 
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newArray = arrayMove(items, oldIndex, newIndex);
      setItems(newArray);
 
      try {
        setSaving(true);
        await axios.put(
          CONST.UPDATE_QUESTION_ORDER,
          {
            question_id: active.id,
            technology_id: technologyId,
            version_id: selectedVersion,
            new_order: newIndex + 1,
          },
          { headers: { Authorization: `Bearer ${isToken}` } },
        );
      } catch (error) {
        console.error("Update failed", error);
        fetchApprovedQuestions(); // Rollback
      } finally {
        setSaving(false);
      }
    }
  };
 
  return (
    <Modal show={show} onHide={handleClose} fullscreen={true}>
      <Modal.Header closeButton className=" text-secondary">
        <Modal.Title>
          Reorder Articles: {techName}
          {saving && (
            <span className="ms-3 fs-6 fw-normal opacity-75">
              Saving Changes...
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light d-flex justify-content-center align-items-center p-3 " style={{ minHeight: "calc(100vh - 70px)" }}>
          {/* Inner Card Container */}
          <div 
            className="border border-1 shadow-sm p-4 bg-white d-flex flex-column shadow-sm comment-item-hover" 
            style={{ width: "100%", maxWidth: "1000px", minHeight: "85vh" }}
          >
            
            {/* Version Selector */}
            <Row className="mb-4 align-items-center">
              <Col sm={4}>
                <Form.Label className="fw-bold">
                  Select Technology Version:
                </Form.Label>
              </Col>
              <Col sm={8}>
                <Form.Select
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                >
                  <option value="">-- Choose a Version --</option>
                  {allVersions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {/* Info Alert */}
            <div className="alert alert-info shadow-sm mb-3 align-items-center justify-content-center d-flex comment-item-hover">
              Drag and drop items to change sequence. Orders are updated for{" "}
              <strong>Version {selectedVersion}</strong>.
            </div>

            {/* Scrollable Question List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                maxHeight: "650px",
                paddingRight: "5px",
              }}
            >
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <ListGroup>
                    {items.length > 0 ? (
                      items.map((item, index) => (
                        <SortableItem
                          key={item.id}
                          id={item.id}
                          title={item.title}
                          index={index}
                        />
                      ))
                    ) : (
                      <div className="text-center p-5 text-muted border rounded">
                        {selectedVersion
                          ? "No approved articles found for this version."
                          : "Please select a version above to view articles."}
                      </div>
                    )}
                  </ListGroup>
                </SortableContext>
              </DndContext>
            </div>

            {/* Footer Button */}
            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </div>

          </div>
        </Modal.Body>
      {/* <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer> */}
    </Modal>
  );
}